import React from 'react'
import { GetStaticProps } from 'next';

import StaticContainer from '../../layouts/StaticContainer'
import Meta from '../../layouts/Meta';
import MemberList from '../../components/our-team/MemberList';
import JoinUs from '../../components/our-team/JoinUs';
import { getCmsTeam } from '../../services/cms/team';
import { TeamMember } from '../../interfaces';
import { buildTeamItemListSchema } from '../../lib/personSchema';
import { cmsPageProps } from '../../lib/pageProps';
import { useTranslation } from 'next-i18next';

interface Props {
  members: TeamMember[];
}

const OurTeam = ({ members }: Props) => {

  const { t } = useTranslation('our-team');
  // Person/ItemList JSON-LD, built server-side so it lands in the crawlable HTML
  // (the GEO audit flagged Person schema as missing on this page).
  const teamSchema = members.length ? buildTeamItemListSchema(members) : null;

  return (
    <StaticContainer title={t('title')} category="About">
      <Meta title={t('title')} description={t('meta-description')}  keywords={t('meta-keyword')}/>
      {teamSchema && (
        <script
          type="application/ld+json"
          // Escape `<` so a "</script>" inside CMS-sourced fields cannot break out.
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(teamSchema).replace(/</g, "\\u003c"),
          }}
        />
      )}
      <MemberList members={members} t={t} loading={false} />
      <JoinUs t={t}/>
    </StaticContainer>
  )
}

// Fetch members at build time (was a client-side useEffect) so the team list and
// its Person schema are server-rendered. ISR keeps it near-live for team changes.
// Members now come from the SOLS Group CMS `publicTeam` node (see
// services/cms/team.ts) instead of Foundation's own `pagesList/our-team`.
export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const members: TeamMember[] = await getCmsTeam();

  return {
    props: {
      members,
      ...(await cmsPageProps(locale, ['our-team'])),
    },
    revalidate: 60,
  };
}

export default OurTeam
